// GitHub API wrapper using authenticated axios instance
import api from '../services/api';

/**
 * Helper function to make API calls using the authenticated axios instance
 * This ensures all GitHub API calls include Stytch authentication headers
 */
async function apiCall(endpoint: string, options: { method?: string; body?: any } = {}) {
  console.log(`📞 GitHub API: ${options.method || 'GET'} ${endpoint}`, options.body ? `with body` : '');

  try {
    let response;
    const method = (options.method || 'GET').toUpperCase();
    
    switch (method) {
      case 'POST':
        response = await api.post(endpoint, options.body);
        break;
      case 'PUT':
        response = await api.put(endpoint, options.body);
        break;
      case 'DELETE':
        response = await api.delete(endpoint);
        break;
      case 'GET':
      default:
        response = await api.get(endpoint);
        break;
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`GitHub API Error: ${error.message}`, error.response?.data);
    throw error;
  }
}

export const githubApi = {
  async health() {
    return apiCall('/github/health');
  },

  async listRepos(org: string) {
    return apiCall(`/github/repos?org=${encodeURIComponent(org)}`) as Promise<{ id: number; name: string; private: boolean }[]>;
  },

  async getInstallUrl() {
    const data = await apiCall('/github/app/install-url');
    return data.url as string;
  },

  async isConnected() {
    try {
      const data = await apiCall('/github/app/connected');
      return data; // Return full object with connected, installationId, urls
    } catch (error) {
      // If we can't connect to check status, assume not connected
      console.warn('Failed to check GitHub connection status:', error);
      return { connected: false, installationId: null, urls: null };
    }
  },

  async disconnect() {
    try {
      const data = await apiCall('/github/app/disconnect');
      return data;
    } catch (error) {
      console.error('Failed to disconnect GitHub:', error);
      throw error;
    }
  },

  async completeInstallation(installationId: string) {
    try {
      const data = await apiCall('/github/app/complete-installation', {
        method: 'POST',
        body: { installationId },
      });
      return data; // Should return { success: true, message: ..., installationId: ..., organizationName: ... }
    } catch (error) {
      console.error('Failed to complete GitHub installation with backend:', error);
      throw error; // Re-throw to be handled by the caller
    }
  },

  async listRepositories() {
    try {
      const data = await apiCall('/github/user/repos');
      return data as Array<{
        id: number;
        name: string;
        fullName: string;
        owner: string;
        description: string | null;
        url: string;
        language: string;
        stars: number;
        forks: number;
        isPrivate: boolean;
      }>;
    } catch (error) {
      console.error('Failed to fetch GitHub repositories:', error);
      return [];
    }
  },

  /**
   * Search repositories - backend handles caching and cache misses
   * First call triggers cache population, subsequent calls use in-memory cache
   * On cache miss, backend automatically refreshes from GitHub API
   */
  async searchRepositories(query: string) {
    try {
      const data = await apiCall(`/github/search-repos?query=${encodeURIComponent(query)}`);
      return data;
    } catch (error) {
      console.error('Failed to search repositories:', error);
      return { total: 0, items: [] };
    }
  },

  async getRepository(owner: string, repo: string) {
    try {
      return await apiCall(`/github/repos/${owner}/${repo}`);
    } catch (error) {
      console.error('Failed to get repository:', error);
      throw error;
    }
  },

  async linkRepoToNode(nodeId: string, mindmapId: string, repo: any) {
    try {
      return await apiCall(`/github/nodes/${nodeId}/link`, {
        method: 'POST',
        body: {
          mindmapId,
          repositoryUrl: repo.url || repo.html_url,
          repositoryOwner: repo.owner,
          repositoryName: repo.name,
          repositoryFullName: repo.fullName || repo.full_name,
          metadata: {
            description: repo.description,
            language: repo.language,
            stars: repo.stars || repo.stargazers_count,
            forks: repo.forks || repo.forks_count,
            isPrivate: repo.isPrivate || repo.private,
          },
        },
      });
    } catch (error) {
      console.error('Failed to link repository:', error);
      throw error;
    }
  },

  async getNodeLinks(nodeId: string) {
    try {
      return await apiCall(`/github/nodes/${nodeId}/links`);
    } catch (error) {
      console.error('Failed to get node links:', error);
      return [];
    }
  },

  async removeLink(linkId: string) {
    try {
      return await apiCall(`/github/links/${linkId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to remove link:', error);
      throw error;
    }
  },

  async refreshMetadata(linkId: string) {
    try {
      return await apiCall(`/github/links/${linkId}/refresh`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to refresh metadata:', error);
      throw error;
    }
  },

  /**
   * Set Personal Access Token for GitHub integration
   */
  async setPAT(token: string) {
    try {
      return await apiCall('/github/app/pat', {
        method: 'POST',
        body: { token },
      });
    } catch (error) {
      console.error('Failed to set PAT:', error);
      throw error;
    }
  },

  /**
   * Remove Personal Access Token
   */
  async removePAT() {
    try {
      return await apiCall('/github/app/pat', { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to remove PAT:', error);
      throw error;
    }
  },
}; 